import { getEarnTag, wishlistsApi } from "@capone/common";
import { STAYS_WISHLIST } from "@capone/experiments";
import { Box, IconButton, Link, Tooltip, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  ActionButton,
  HotelInformationalBanner,
  Icon,
  IconName,
  PremierCollectionAvailabilityCard,
  PremierCollectionBenefitsModal,
  ensureExhaustive,
  getIsFreeBreakfast,
  textConstants as halifaxTextConstants,
  isSpendAndGetOffer,
} from "halifax";
import React from "react";
import { InView } from "react-intersection-observer";
import ReactList from "react-list";
import { useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";
import {
  ENGAGED_OFFER_CTA,
  IIdLodgings,
  ListingCollectionEnum,
  ListingSearchResult,
  Lodging,
  LodgingCollectionEnum,
  RewardsCurrency,
  SelectedTravelOfferScreen,
  StayTypesEnum,
  VIEWED_LODGING_IN_LIST,
  VRAvailabilityResultEnum,
  getLodgingTrackingProperties,
  getNthNightTrackingProperty,
} from "redmond";
import { StayType } from "redmond/hotels-module/interfaces";
import { config } from "../../../../api/config";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  TRAVEL_WALLET_OFFER_EXPERIMENT,
  LUXURY_PAY_AT_HOTEL_BADGE,
  ALLOWED_PAY_LATER_LODGINGS,
  AllowedPayLaterLodgingsConfig,
  getExperimentVariant,
  getExperimentVariantJSON,
  useExperiments,
} from "../../../../context/experiments";
import { isAllowedPayLaterLodging } from "../../../shop/utils/payLater";
import { WishlistToggleHeart } from "../../../wishlist";
import { PremierCollectionAvailabilityCallState } from "../../reducer";
import { AvailabilityMap } from "../AvailabilityMap";
import { PremierCollectionAvailabilitySearchControl } from "../AvailabilitySearchControl";
import { AvailabilitySort } from "../AvailabilitySort";
import { PremierCollectionHotelFunnelCTA } from "../HotelFunnelCTA";
import { AvailabilityListConnectorProps } from "./container";
import "./styles.scss";
import * as textConstants from "./textConstants";

export interface IAvailabilityListProps
  extends AvailabilityListConnectorProps,
  RouteComponentProps {
  isMobile?: boolean;
  isVRForPremiumCardHoldersEnabled: boolean;
  isWithLifestyleCollection: boolean;
  isWithNonPremiumLifestyleCollection: boolean;
  isXmodRedesign?: boolean;
  isDatelessSearchUnavailableEnabled?: boolean;
  openExpandedMap?: () => void;
  navigateToProperty: (
    property: Lodging | ListingSearchResult,
    index: number
  ) => void;
  fetchInitial: () => void;
}

export const PremierCollectionAvailabilityList = (
  props: IAvailabilityListProps
) => {
  const {
    // Context
    isMobile,
    stayType,
    credit,
    nightCount,
    history,
    searchLocation,
    mapBounds,
    fromDate,
    untilDate,
    // Property selection
    setPropertyIdInFocus,
    setPropertyIdHovered,
    setSelectedLodgingIndex,
    // Outer state
    navigateToProperty,
    openExpandedMap,
    // Accounts
    accountReferenceId,
    selectedAccount,
    largestValueAccount,
    // Availability Management
    vacationRentalsListings,
    lodgings,
    callState,
    isFetchingInitialAvailability,
    isStillFetchingInFollowUpCalls,
    fetchInitial,
    fetchMorePremierCollectionAvailability,
    fetchMoreVacationRentalsAvailability,
    // Tracking
    viewedPremierCollectionListProperties,
    viewedVacationRentalListProperties,
    // Experiments
    isVRForPremiumCardHoldersEnabled,
    isWithLifestyleCollection,
    isWithNonPremiumLifestyleCollection,
    isXmodRedesign,
    isDatelessSearchUnavailableEnabled,
  } = props;
  const [openBenefitsModal, setOpenBenefitsModal] = React.useState(false);
  const [benefitsModalVariant, setBenefitsModalVariant] = React.useState<
    | "premier-collection"
    | "lifestyle-collection-premium"
    | "lifestyle-collection-non-premium"
  >();
  const expState = useExperiments();

  const travelWalletOffer = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_OFFER_EXPERIMENT
  );
  const isTravelWalletOfferExperiment = React.useMemo(
    () => travelWalletOffer === AVAILABLE,
    [travelWalletOffer]
  );

  const wishlistExperiment = getExperimentVariant(
    expState.experiments,
    STAYS_WISHLIST
  );
  const isWishlistExperiment = React.useMemo(
    () => wishlistExperiment === AVAILABLE,
    [wishlistExperiment]
  );

  const isPayAtHotelBadgeEnabled = React.useMemo(
    () =>
      getExperimentVariant(expState.experiments, LUXURY_PAY_AT_HOTEL_BADGE) ===
      AVAILABLE,
    [expState.experiments]
  );

  const allowedPayLaterLodgingIds = React.useMemo(() => {
    const config = getExperimentVariantJSON(
      expState.experiments,
      ALLOWED_PAY_LATER_LODGINGS
    ) as AllowedPayLaterLodgingsConfig | null;
    return config?.data?.lodgingIds;
  }, [expState.experiments]);

  const favoritedLodgings = useSelector(
    wishlistsApi.endpoints.getAllWishlistItemsByUser.select()
  )?.data;

  const isFavorited = (curr: Lodging) => {
    return favoritedLodgings?.has(curr.lodging.id);
  };

  const listRef = React.useRef<ReactList | null>(null);
  const divRef = React.useRef<HTMLDivElement | null>(null);
  const headerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (headerRef.current && !isFetchingInitialAvailability) {
      headerRef.current.focus();
    }
  }, [isFetchingInitialAvailability]);

  const locationName = React.useMemo(
    () => (mapBounds != null ? "Map area" : searchLocation?.label ?? ""),
    [mapBounds == null, searchLocation?.label]
  );

  React.useEffect(() => {
    if (
      ![
        PremierCollectionAvailabilityCallState.FollowUpSearchCallSuccess,
        PremierCollectionAvailabilityCallState.InitialSearchCallSuccess,
      ].includes(callState)
    ) {
      return;
    }
    switch (stayType) {
      case StayTypesEnum.Hotels: {
        fetchMorePremierCollectionAvailability(
          history,
          isWithNonPremiumLifestyleCollection
            ? LodgingCollectionEnum.Lifestyle
            : LodgingCollectionEnum.Premier
        );
        break;
      }
      case StayTypesEnum.VacationRentals: {
        fetchMoreVacationRentalsAvailability(history);
        break;
      }
      default:
        ensureExhaustive(stayType);
        break;
    }
  }, [
    history,
    callState,
    fetchMorePremierCollectionAvailability,
    fetchMoreVacationRentalsAvailability,
  ]);

  const isLoading =
    isFetchingInitialAvailability || isStillFetchingInFollowUpCalls;

  const isAvail = (curr: Lodging) => curr.available ?? true;

  const availableSort = (a: Lodging, b: Lodging) => {
    if (isAvail(a) && isAvail(b)) {
      return 0;
    }
    if (isAvail(b)) {
      return 1;
    }
    if (isAvail(a)) {
      return -1;
    }
    return 0;
  };

  // sort lodgings/VR listings
  const sortedProperties = React.useMemo(() => {
    if (stayType === StayTypesEnum.VacationRentals) {
      return vacationRentalsListings?.length ? vacationRentalsListings : [];
    }
    return lodgings.sort(availableSort);
  }, [lodgings, stayType, vacationRentalsListings]);

  const numberOfResults =
    stayType === StayTypesEnum.VacationRentals
      ? vacationRentalsListings?.length ?? 0
      : lodgings.length;

  const commonStayType = React.useMemo(() => {
    switch (stayType) {
      case StayTypesEnum.VacationRentals:
        return StayType.Homes;
      case StayTypesEnum.Hotels:
        return StayType.Hotels;
      default:
        return ensureExhaustive(stayType);
    }
  }, [stayType]);

  const renderItem = (
    property: ListingSearchResult | Lodging,
    index: number
  ) => {
    const linkId = `availability-row-${index}`;
    if ("lodging" in property) {
      const { lodging } = property;
      const freeBreakfast =
        property.lodgingCollection === LodgingCollectionEnum.Lifestyle &&
        getIsFreeBreakfast(property.price?.mealPlan?.kind);
      return (
        <Link
          id={linkId}
          onMouseEnter={() => setPropertyIdHovered(lodging.id)}
          onMouseLeave={() => setPropertyIdHovered(null)}
          onFocus={(event) => {
            if (event.target.id === linkId) {
              setPropertyIdInFocus(lodging.id);
            }
          }}
          className={clsx("availability-row-result", {
            unavailable:
              !property.available && !isDatelessSearchUnavailableEnabled,
            "free-breakfast": freeBreakfast && !property.nthNightPromotion,
            "nth-night": property.nthNightPromotion,
          })}
          component="a"
          key={lodging.id}
          onClick={() => {
            if (property.available || isDatelessSearchUnavailableEnabled) {
              setSelectedLodgingIndex(index);
              navigateToProperty(property, index);
            }
          }}
        >
          {!isMobile && property.nthNightPromotion ? (
            <HotelInformationalBanner
              content={
                <Tooltip
                  title={
                    <Typography className="tooltip-text">
                      {property.nthNightPromotion?.tooltipDescription}
                    </Typography>
                  }
                  classes={{
                    popper: "nth-night-tooltip-popper",
                    tooltip: "nth-night-tooltip-text",
                  }}
                  disableFocusListener={false}
                  aria-labelledby={
                    property.nthNightPromotion?.tooltipDescription
                  }
                >
                  <Box className="nth-night-banner-content">
                    <Typography className="nth-night-content-text">
                      {property.nthNightPromotion?.description}
                    </Typography>
                    <IconButton aria-labelledby="nth-night-info-label">
                      <Icon name={IconName.InfoCircle} />
                    </IconButton>
                  </Box>
                </Tooltip>
              }
              isMobile={false}
              isMap={false}
              iconName={IconName.Sparkle}
              isXmodRedesign={isXmodRedesign}
            />
          ) : null}
          {!isMobile && freeBreakfast && !property.nthNightPromotion ? (
            <HotelInformationalBanner
              content={
                <Tooltip
                  title={
                    <Typography className="tooltip-text">
                      {halifaxTextConstants.FREE_BREAKFAST_TOOLTIP}
                    </Typography>
                  }
                  classes={{
                    popper: "free-breakfast-tooltip-popper",
                    tooltip: "free-breakfast-tooltip-text",
                  }}
                >
                  <Box className="free-breakfast-banner-content">
                    <Typography className="free-breakfast-content-text">
                      Free breakfast for cardholders
                    </Typography>
                    <Icon name={IconName.InfoCircle} />
                  </Box>
                </Tooltip>
              }
              isMobile={false}
              isMap={false}
              iconName={IconName.FreeBreakfast}
              isXmodRedesign={isXmodRedesign}
            />
          ) : null}
          <InView
            as="div"
            onChange={(inView) => {
              if (inView) {
                const nightlyTotalWithTaxesAndFees =
                  property.price?.breakdown &&
                    "taxesAndFees" in property.price?.breakdown
                    ? (property.price?.nightlyPrice.fiat.value || 0) +
                    (property.price?.breakdown.taxesAndFees?.fiat.value ||
                      0) /
                    (nightCount || 1)
                    : property.price?.nightlyPrice.fiat.value || 0;

                const searchedLodgingSelection = (
                  searchLocation?.id as IIdLodgings
                )?.lodgingSelection;

                let isSearchedLodging = false;

                if (
                  searchedLodgingSelection &&
                  searchedLodgingSelection.placeTypes?.includes("lodging")
                ) {
                  const searchTermName =
                    searchedLodgingSelection.searchTerm.split(",")[0];

                  isSearchedLodging =
                    property.lodging.name.startsWith(searchTermName);
                }

                trackEvent({
                  eventName: VIEWED_LODGING_IN_LIST,
                  properties: {
                    ...viewedPremierCollectionListProperties.properties,
                    ...getLodgingTrackingProperties(property).properties,
                    lodging_row_index: index,
                    lodging_id: lodging.id,
                    lodging_name: lodging.name,
                    lodging_tripadvisor_rating:
                      lodging.tripAdvisorReviews?.overallScore.toString(),
                    promotion_applicable:
                      property?.bestPromotionThisLodging?.promotionType,
                    is_preferred: property?.isPreferred,
                    is_available: property?.available,
                    google_place_id: searchedLodgingSelection?.placeId,
                    daily_owed_including_taxes_fees_usd:
                      nightlyTotalWithTaxesAndFees,
                    daily_owed_excluding_taxes_fees_usd:
                      property.price?.nightlyPrice.fiat.value,
                    is_compare_to_bar: false,
                    is_searched: isSearchedLodging,
                    is_vr: false,
                    is_favorited: isFavorited(property),
                    free_cancellation_shown: property.isFreeCancel,
                    check_in_date: fromDate
                      ? dayjs(fromDate).format("YYYY-MM-DD")
                      : undefined,
                    check_out_date: untilDate
                      ? dayjs(untilDate).format("YYYY-MM-DD")
                      : undefined,
                    los:
                      fromDate && untilDate
                        ? dayjs(untilDate).diff(fromDate, "day")
                        : undefined,
                    funnel: "premium_stays",
                    nth_night_promo: getNthNightTrackingProperty(property),
                    has_pay_at_stay: property?.highlights?.hasPayLater,
                  },
                  encryptedProperties: [
                    ...viewedPremierCollectionListProperties.encryptedProperties,
                    ...getLodgingTrackingProperties(property)
                      .encryptedProperties,
                  ],
                });
              }
            }}
            threshold={0.8}
          >
            <PremierCollectionAvailabilityCard
              hotelAvailabilityInfo={{
                ...property,
              }}
              isLifestyleCollection={
                (isWithLifestyleCollection ||
                  isWithNonPremiumLifestyleCollection) &&
                property.lodgingCollection === LodgingCollectionEnum.Lifestyle
              }
              isXmodRedesign={isXmodRedesign}
              isPayAtHotelBadgeEnabled={isPayAtHotelBadgeEnabled && isAllowedPayLaterLodging(allowedPayLaterLodgingIds, lodging.id)}
              isMobile={isMobile}
              showHotelDetailsCTA={false}
              hotelUnavailableTitleText={textConstants.getMinNightStayHotelUnavailTitle(
                nightCount,
                property.minNightOfStay
              )}
              hotelUnavailableMessage={textConstants.getMinNightStayHotelUnavailMessage(
                nightCount,
                property.minNightOfStay
              )}
              showRibbon={
                isWithLifestyleCollection ||
                (!isMobile && isWithNonPremiumLifestyleCollection)
              }
              showOffer={
                isTravelWalletOfferExperiment &&
                property.bestOfferThisLodging?.amount.amount !==
                  credit?.amount.amount &&
                !(
                  property.bestOfferThisLodging &&
                  isSpendAndGetOffer(
                    property.bestOfferThisLodging.descriptions
                  )
                )
              }
              nightCount={nightCount}
              rewardsKey={accountReferenceId}
              available={property.available}
              tenant={config.TENANT}
              vrBenefitsTooltipContent={
                <Box>
                  <Typography variant="subtitle2" className="tooltip-text">
                    {selectedAccount?.productDisplayName
                      ? `• Earn 5x on your ${selectedAccount?.productDisplayName
                      } card and enjoy a $${property.lodgingCollection ===
                        LodgingCollectionEnum.Lifestyle
                        ? 50
                        : 100
                      } Experience Credit to use during your stay`
                      : ""}
                  </Typography>
                  <Typography variant="subtitle2" className="tooltip-text">
                    • Plus, enjoy other premium benefits like early check-in and
                    late checkout when available
                  </Typography>
                </Box>
              }
              vrBenefitsMobilePopoverContent={
                <Box>
                  <Typography
                    variant="subtitle2"
                    className="mobile-vr-benefits-popover-content"
                  >
                    {selectedAccount?.productDisplayName
                      ? `• Earn 5x on your ${selectedAccount?.productDisplayName
                      } card and enjoy a $${property.lodgingCollection ===
                        LodgingCollectionEnum.Lifestyle
                        ? 50
                        : 100
                      } Experience Credit to use during your stay`
                      : ""}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    className="mobile-vr-benefits-popover-content"
                  >
                    • Plus, enjoy other premium benefits like early check-in and
                    late checkout when available
                  </Typography>
                </Box>
              }
              className={clsx({
                "free-breakfast": freeBreakfast && !property.nthNightPromotion,
                "nth-night": property.nthNightPromotion,
              })}
              earnTagContent={
                selectedAccount != null ? (
                  <>
                    <Icon name={IconName.StarIcon} />
                    <Typography className="earn-tag-text">
                      {getEarnTag(
                        selectedAccount,
                        selectedAccount.rewardsBalance
                          .currency as RewardsCurrency,
                        "hotels"
                      )}
                    </Typography>
                  </>
                ) : undefined
              }
              earnTagClassName="b2b"
              onBenefitsTooltipClick={() => {
                setBenefitsModalVariant(
                  (() => {
                    if (
                      property.lodgingCollection ===
                      LodgingCollectionEnum.Lifestyle
                    ) {
                      if (isWithLifestyleCollection)
                        return "lifestyle-collection-premium";
                      return "lifestyle-collection-non-premium";
                    }
                    return "premier-collection";
                  })()
                );
                setOpenBenefitsModal(true);
              }}
              tooltipContent={
                <>
                  <Box className="benefits-summary">
                    <Icon name={IconName.StarOutline} />
                    <Typography className="benefits-summary-text">
                      {textConstants.getBenefitsSummary(
                        property.lodgingCollection ??
                        LodgingCollectionEnum.NoCollection,
                        selectedAccount?.productDisplayName,
                        largestValueAccount.earn?.hotelsMultiplier
                      )}
                    </Typography>
                  </Box>
                  <Box className="additional-benefits">
                    <Icon name={IconName.GiftOutline} />
                    <Typography className="additional-benefits-summary-text">
                      {textConstants.getAdditionalBenefitsSummary()}
                    </Typography>{" "}
                  </Box>
                </>
              }
              onOfferTooltipSetOpenModal={(open) => {
                if (open) {
                  trackEvent({
                    eventName: ENGAGED_OFFER_CTA,
                    properties: {
                      location: SelectedTravelOfferScreen.PREMIUM_STAYS_SHOP,
                      entry_type: "tooltip",
                      funnel: property.bestOfferThisLodging?.funnels.join(","),
                      offer_name:
                        property.bestOfferThisLodging?.trackingPropertiesV2
                          ?.properties?.offer_name,
                    },
                    encryptedProperties: [
                      property.bestOfferThisLodging?.trackingPropertiesV2
                        ?.encryptedProperties ?? "",
                    ],
                  });
                }
              }}
              wishlistComponent={
                isWishlistExperiment ? (
                  <WishlistToggleHeart
                    listingImageUrl={lodging.media?.[0]?.url}
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "8px",
                    }}
                    listingId={lodging.id}
                    stayType={commonStayType}
                    collection={property.lodgingCollection}
                  />
                ) : undefined
              }
            />
          </InView>
        </Link>
      );
    }
    const { listingId } = property;
    return (
      <Link
        id={linkId}
        className="availability-row-result"
        component="a"
        key={listingId.toString()}
        onMouseEnter={() => setPropertyIdHovered(listingId.id)}
        onMouseLeave={() => setPropertyIdHovered(null)}
        onFocus={(event) => {
          if (event.target.id === linkId) {
            setPropertyIdInFocus(listingId.id);
          }
        }}
        onClick={() => navigateToProperty(property, index)}
      >
        <InView
          as="div"
          onChange={(inView) => {
            if (inView) {
              const searchedLodgingSelection = (
                searchLocation?.id as IIdLodgings
              )?.lodgingSelection;

              const baseTrackingProperties =
                stayType === StayTypesEnum.VacationRentals
                  ? viewedVacationRentalListProperties
                  : viewedPremierCollectionListProperties;

              trackEvent({
                eventName: VIEWED_LODGING_IN_LIST,
                properties: {
                  ...baseTrackingProperties.properties,
                  lodging_row_index: index,
                  lodging_id: listingId.id,
                  lodging_name: property.listing.content.name,
                  is_available:
                    property.availability.AvailabilityResult ===
                    VRAvailabilityResultEnum.Available,
                  google_place_id: searchedLodgingSelection?.placeId,
                  is_vr: stayType,
                  check_in_date: fromDate
                    ? dayjs(fromDate).format("YYYY-MM-DD")
                    : undefined,
                  check_out_date: untilDate
                    ? dayjs(untilDate).format("YYYY-MM-DD")
                    : undefined,
                  los:
                    fromDate && untilDate
                      ? dayjs(untilDate).diff(fromDate, "day")
                      : undefined,
                  funnel: "premium_stays",
                  product_type: property.listing.listingCollection,
                  has_reviews: (property.listing.content.userRating?.numberOfReviews ?? 0) > 0,
                },
                encryptedProperties: [
                  ...baseTrackingProperties.encryptedProperties,
                ],
              });
            }
          }}
          threshold={0.8}
        >
          <PremierCollectionAvailabilityCard
            home={property}
            showVRBenefits
            className={clsx({
              "wishlist-enabled": isWishlistExperiment,
            })}
            isXmodRedesign={isXmodRedesign}
            isPayAtHotelBadgeEnabled={isPayAtHotelBadgeEnabled}
            earnTagContent={
              selectedAccount != null ? (
                <>
                  <Icon name={IconName.StarIcon} />
                  <Typography className="earn-tag-text">
                    {getEarnTag(
                      selectedAccount,
                      selectedAccount.rewardsBalance
                        .currency as RewardsCurrency,
                      "vacation rentals"
                    )}
                  </Typography>
                </>
              ) : undefined
            }
            earnTagClassName="b2b"
            wishlistComponent={
              isWishlistExperiment && property.listingId.id ? (
                <WishlistToggleHeart
                  listingId={property.listingId.id}
                  listingImageUrl={property.listing.content.media?.[0]?.url}
                  stayType={StayType.Homes}
                  collection={property.listing.listingCollection}
                />
              ) : undefined
            }
            // isSkeleton={isLoading}
            isMobile={isMobile}
            showHotelDetailsCTA={
              property.availability.AvailabilityResult ===
              VRAvailabilityResultEnum.Available
            }
            showRibbon={
              isWithLifestyleCollection ||
              (!isMobile && isWithNonPremiumLifestyleCollection)
            }
            showOffer={
              isTravelWalletOfferExperiment &&
              property.bestOffer?.amount.amount !== credit?.amount.amount
            }
            ctaText="View home details"
            isLifestyleCollection={
              property.listing.listingCollection ===
              ListingCollectionEnum.Lifestyle
            }
            nightCount={nightCount}
            rewardsKey={accountReferenceId}
            available={
              property.availability.AvailabilityResult ===
              VRAvailabilityResultEnum.Available
            }
            hotelUnavailableTitleText="Home Unavailable"
            onOfferTooltipSetOpenModal={(open) => {
              if (open) {
                trackEvent({
                  eventName: ENGAGED_OFFER_CTA,
                  properties: {
                    location: SelectedTravelOfferScreen.PREMIUM_STAYS_SHOP,
                    entry_type: "tooltip",
                    funnel: property.bestOffer?.funnels.join(","),
                    offer_name:
                      property.bestOffer?.trackingPropertiesV2?.properties
                        ?.offer_name,
                  },
                  encryptedProperties: [
                    property.bestOffer?.trackingPropertiesV2
                      ?.encryptedProperties ?? "",
                  ],
                });
              }
            }}
            vrBenefitsTooltipContent={
              <Box>
                <Typography variant="subtitle2" className="tooltip-text">
                  {selectedAccount?.productDisplayName
                    ? `• Earn 5x on your ${selectedAccount?.productDisplayName
                    } card and enjoy a $${property.listing.listingCollection ===
                      ListingCollectionEnum.Lifestyle
                      ? 50
                      : 100
                    } Experience Credit to use during your stay`
                    : ""}
                </Typography>
                <Typography variant="subtitle2" className="tooltip-text">
                  • Plus, enjoy other premium benefits like early check-in and
                  late checkout when available
                </Typography>
              </Box>
            }
            vrBenefitsMobilePopoverContent={
              <Box>
                <Typography
                  variant="subtitle2"
                  className="mobile-vr-benefits-popover-content"
                >
                  {selectedAccount?.productDisplayName
                    ? `• Earn 5x on your ${selectedAccount?.productDisplayName
                    } card and enjoy a $${property.listing.listingCollection ===
                      ListingCollectionEnum.Lifestyle
                      ? 50
                      : 100
                    } Experience Credit to use during your stay`
                    : ""}
                </Typography>
                <Typography
                  variant="subtitle2"
                  className="mobile-vr-benefits-popover-content"
                >
                  • Plus, enjoy other premium benefits like early check-in and
                  late checkout when available
                </Typography>
              </Box>
            }
          />
        </InView>
      </Link>
    );
  };

  const renderFooter = () => {
    if (isLoading) {
      return (
        <PremierCollectionAvailabilityCard
          className="premier-collection-availability-card-skeleton"
          isSkeleton
          isMobile={isMobile}
          isXmodRedesign={isXmodRedesign}
          isPayAtHotelBadgeEnabled={isPayAtHotelBadgeEnabled}
        />
      );
    }
    if (
      !isFetchingInitialAvailability &&
      !isStillFetchingInFollowUpCalls &&
      numberOfResults === 0
    ) {
      return (
        <PremierCollectionHotelFunnelCTA
          variant="no-results"
          locationName={locationName}
          isWithNonPremiumLifestyleCollection={
            isWithNonPremiumLifestyleCollection
          }
          isWithLifestyleCollection={isWithLifestyleCollection}
          stayType={stayType}
          isXmodRedesign={isXmodRedesign}
        />
      );
    }
    return (
      <PremierCollectionHotelFunnelCTA
        variant="banner"
        locationName={locationName}
        isWithNonPremiumLifestyleCollection={
          isWithNonPremiumLifestyleCollection
        }
        isWithLifestyleCollection={isWithLifestyleCollection}
        stayType={stayType}
        isXmodRedesign={isXmodRedesign}
      />
    );
  };

  return (
    <Box
      className={clsx("pc-availability-list-root", {
        mobile: isMobile,
        "only-lifestyle-collection": isWithNonPremiumLifestyleCollection,
        xmod: isXmodRedesign,
      })}
    >
      {!isMobile ? (
        <Box className="search-control">
          <PremierCollectionAvailabilitySearchControl
            doSearch={fetchInitial}
            isLifestyleCollection={isWithLifestyleCollection}
            isWithNonPremiumLifestyleCollection={
              isWithNonPremiumLifestyleCollection
            }
          />
        </Box>
      ) : undefined}
      <PremierCollectionBenefitsModal
        openBenefitsModal={openBenefitsModal}
        handleClosePCBenefitsModal={() => {
          setOpenBenefitsModal(false);
        }}
        isMobile={isMobile}
        variant={benefitsModalVariant}
        useBenefitsSummary
        largestValueAccount={largestValueAccount}
      />
      <Box className="availability-list-container">
        {isFetchingInitialAvailability || numberOfResults > 0 ? (
          <Box className="availability-list-header">
            <Typography
              variant="h4"
              className="pc-count-heading"
              ref={headerRef}
            >
              {textConstants.COUNT_HEADING_TEXT(
                numberOfResults,
                locationName,
                isLoading,
                isWithLifestyleCollection,
                isWithNonPremiumLifestyleCollection,
                stayType
              )}
            </Typography>
            {!isMobile && numberOfResults > 1 && <AvailabilitySort />}
          </Box>
        ) : undefined}

        <div ref={divRef} className="availability-list">
          {isMobile && !isFetchingInitialAvailability && numberOfResults > 0 ? (
            <div className="availability-map-preview">
              <AvailabilityMap
                isPreview
                isWithNonPremiumLifestyleCollection={
                  isWithNonPremiumLifestyleCollection
                }
                isVRForPremiumCardHoldersEnabled={
                  isVRForPremiumCardHoldersEnabled
                }
                navigateToProperty={() => undefined}
              />
              <div
                className="availability-map-button-container"
                onClick={openExpandedMap}
              >
                <ActionButton
                  fill="blue"
                  className="show-map-button b2b"
                  onClick={() => undefined}
                  message={
                    <Typography className="toggle-map-button-text">
                      {stayType === StayTypesEnum.VacationRentals
                        ? textConstants.VIEW_HOMES_ON_MAP
                        : textConstants.VIEW_HOTELS_ON_MAP}
                    </Typography>
                  }
                />
              </div>
            </div>
          ) : undefined}
          <ReactList
            ref={listRef}
            itemRenderer={(index) => {
              const property = sortedProperties[index];
              const id =
                "listingId" in property
                  ? property.listingId.id
                  : property.lodging.id;
              return (
                <div key={id} className="availability-list-item">
                  {renderItem(property, index)}
                </div>
              );
            }}
            length={sortedProperties.length}
          />
          {renderFooter()}
        </div>
      </Box>
    </Box>
  );
};
